import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserClasses, Class, updateAllClassDecksPublicFlags } from '../services/classService';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import GroupIcon from '@mui/icons-material/Group';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QrCodeDialog from '../components/QrCodeDialog';

const ClassesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [teacherClasses, setTeacherClasses] = useState<Class[]>([]);
  const [studentClasses, setStudentClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [currentClass, setCurrentClass] = useState<Class | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const fetchClasses = async () => {
      if (user) {
        setLoading(true);
        try {
          await updateAllClassDecksPublicFlags(user.uid);
          
          const { teacherClasses, studentClasses } = await getUserClasses(user.uid);
          setTeacherClasses(teacherClasses);
          setStudentClasses(studentClasses);
        } catch (err) {
          console.error('Error fetching classes:', err);
          setError('Failed to load classes. Please try again later.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchClasses();
  }, [user]);

  if (!user) {
    navigate('/');
    return null;
  }

  const hasClasses = teacherClasses.length > 0 || studentClasses.length > 0;

  // QR Code handlers
  const handleOpenQrDialog = (classItem: Class) => {
    setCurrentClass(classItem);
    setQrDialogOpen(true);
  };

  const handleCloseQrDialog = () => {
    setQrDialogOpen(false);
    setCurrentClass(null);
  };

  const handleSnackbarMessage = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{t('classes.myClasses')}</Typography>
          <Button
            variant="contained"
            startIcon={!isMobile ? <AddIcon /> : undefined}
            onClick={() => navigate('/classes/new')}
          >
            {!isMobile ? t('classes.createClass') : <AddIcon />}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : !hasClasses ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('classes.noClasses')}
            </Typography>
            <Typography color="text.secondary">
              {t('classes.noClassesDescription')}
            </Typography>
          </Paper>
        ) : (
          <>
            {teacherClasses.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <SchoolIcon sx={{ mr: 1 }} /> {t('classes.myOrganizedClasses')}
                </Typography>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)'
                  },
                  gap: 2
                }}>
                  {teacherClasses.map((classItem) => (
                    <Card key={classItem.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {classItem.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {classItem.description}
                        </Typography>
                        <Typography variant="body2">
                          {t('classes.studentsCount')}: {classItem.students?.length || 0}
                        </Typography>
                        <Typography variant="body2">
                          {t('classes.decksCount')}: {classItem.deckIds?.length || 0}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'space-between' }}>
                        <Button 
                          size="small" 
                          onClick={() => navigate(`/classes/${classItem.id}`)}
                        >
                          {t('classes.viewDetails')}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<QrCodeIcon />}
                          onClick={() => handleOpenQrDialog(classItem)}
                        >
                          {t('classes.showQrCode')}
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {studentClasses.length > 0 && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <GroupIcon sx={{ mr: 1 }} /> {t('classes.youveJoined')}
                </Typography>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)'
                  },
                  gap: 2
                }}>
                  {studentClasses.map((classItem) => (
                    <Card key={classItem.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {classItem.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {classItem.description}
                        </Typography>
                        <Typography variant="body2">
                          {t('classes.teacher')}: {classItem.teacherName || t('classes.unknownTeacher')}
                        </Typography>
                        <Typography variant="body2">
                          {t('classes.decksCount')}: {classItem.deckIds?.length || 0}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          onClick={() => navigate(`/classes/${classItem.id}`)}
                        >
                          {t('classes.viewDetails')}
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        {/* QR Code Dialog */}
        {currentClass && (
          <QrCodeDialog 
            open={qrDialogOpen}
            onClose={handleCloseQrDialog}
            url={window.location.origin + `/classes/${currentClass.id}`}
            title={t('classes.qrCodeForClass')}
            onSnackbarMessage={handleSnackbarMessage}
            className={currentClass.id}
          />
        )}

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={notification.severity}
            onClose={handleCloseNotification}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default ClassesPage; 